<script setup lang="ts">
import type { Component } from 'vue'
import AppButton from './AppButton.vue'
import type { PaletteHue } from '../palette'

/**
 * Shared soft-accent empty state — tinted icon circle + title + body + optional
 * solid CTA. Pass a class `hue` to tint with the class colour, otherwise it falls
 * back to the indigo soft-accent (`--info-*`). Use the `cta` prop for the common
 * single-button case, or the `#action` slot for a custom button.
 */
withDefaults(
  defineProps<{
    icon?: Component
    title: string
    body?: string
    hue?: PaletteHue
    cta?: string
    ctaIcon?: Component
    dense?: boolean
  }>(),
  { icon: undefined, body: undefined, hue: undefined, cta: undefined, ctaIcon: undefined, dense: false },
)
defineEmits<{ action: [] }>()
</script>

<template>
  <div class="empty" :class="{ 'empty--dense': dense }">
    <span
      v-if="icon"
      class="empty__icon"
      :style="
        hue
          ? { background: hue.soft, color: hue.ink }
          : { background: 'var(--info-soft)', color: 'var(--info-ink)' }
      "
    >
      <component :is="icon" :size="26" />
    </span>
    <div class="empty__title">{{ title }}</div>
    <div v-if="body" class="empty__body">{{ body }}</div>
    <div v-if="$slots.action || cta" class="empty__cta">
      <slot name="action">
        <AppButton variant="primary" size="sm" @click="$emit('action')">
          <component :is="ctaIcon" v-if="ctaIcon" :size="14" /> {{ cta }}
        </AppButton>
      </slot>
    </div>
  </div>
</template>

<style scoped>
.empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 64px 20px;
  font-family: var(--font-sans);
}
.empty--dense {
  padding: 28px 20px;
}
.empty--dense .empty__icon {
  width: 44px;
  height: 44px;
  border-radius: 13px;
  margin-bottom: 12px;
}
.empty--dense .empty__title {
  font-size: 15px;
}
.empty__icon {
  width: 56px;
  height: 56px;
  border-radius: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
}
.empty__title {
  font-size: 18px;
  font-weight: 600;
  letter-spacing: -0.2px;
  color: var(--text-primary);
}
.empty__body {
  margin-top: 6px;
  max-width: 340px;
  font-size: 14px;
  line-height: 1.5;
  color: var(--ink-500);
}
.empty__cta {
  margin-top: 18px;
}
</style>
